import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2, CircleParking, MessageSquare, Plus, ReceiptText } from "lucide-react";
import { DashboardShell, StatCard, PlaceholderCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { PropertyCard } from "@/components/PropertyCard";
import { ParkingCard } from "@/components/ParkingCard";
import { useAuth } from "@/lib/auth";
import { fetchMyParking, projectParkingToViewport } from "@/lib/parking";
import { fetchInvoices, formatMoney } from "@/lib/invoices";
import { supabase } from "@/integrations/supabase/client";
import { projectToViewport, type DbProperty } from "@/lib/properties";
import {
  SERVICES,
  SERVICE_LABEL,
  SERVICE_STATUS_META,
  isServiceLive,
  useMyServices,
  type ServiceType,
} from "@/lib/services";

export const Route = createFileRoute("/_authenticated/partner/dashboard")({
  head: () => ({
    meta: [
      { title: "Partner dashboard · Landoo" },
      {
        name: "description",
        content: "Manage your active Landoo services, listings, invoices and enquiries.",
      },
    ],
  }),
  component: PartnerDashboard,
});

function PartnerDashboard() {
  const { user } = useAuth();
  const { profiles } = useMyServices();

  const activeTypes = profiles.filter(isServiceLive).map((p) => p.service_type);
  const canList = (t: ServiceType) => activeTypes.includes(t);
  const propertyServicesActive = activeTypes.some((t) => t !== "parking");

  const { data: props = [] } = useQuery({
    queryKey: ["partner", "properties", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("properties").select("*").eq("owner_id", user!.id);
      return (data as unknown as DbProperty[]) ?? [];
    },
    enabled: !!user,
  });

  const { data: parkingRows = [] } = useQuery({
    queryKey: ["partner", "parking", user?.id],
    queryFn: () => fetchMyParking(user!.id),
    enabled: !!user,
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["partner", "invoices", user?.id],
    queryFn: () => fetchInvoices(user!.id),
    enabled: !!user,
  });

  const { data: enquiries = [] } = useQuery({
    queryKey: ["partner", "enquiries", "counts", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("partner_enquiries")
        .select("service_type")
        .eq("partner_id", user!.id);
      return (data as unknown as { service_type: ServiceType }[]) ?? [];
    },
    enabled: !!user,
  });

  const properties = projectToViewport(props);
  const parking = projectParkingToViewport(parkingRows);
  const totalSlots = parkingRows.reduce((s, p) => s + (p.total_slots ?? 0), 0);
  const availableSlots = parkingRows.reduce((s, p) => s + (p.available_slots ?? 0), 0);
  const outstanding = invoices
    .filter((i) => i.payment_status !== "paid" && i.payment_status !== "cancelled")
    .reduce((s, i) => s + Number(i.total || 0), 0);

  const listingCount = (t: ServiceType) =>
    t === "parking"
      ? parkingRows.length
      : props.filter((p) => (p as unknown as { purpose: string }).purpose === t).length;
  const enquiryCount = (t: ServiceType) => enquiries.filter((e) => e.service_type === t).length;

  return (
    <DashboardShell title="Partner dashboard" role="Partner" items={PARTNER_NAV}>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-brown-dark">My services</h2>
        <Link
          to="/partner/services/add"
          className="inline-flex items-center gap-1.5 rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
        >
          <Plus className="h-4 w-4" /> Add new service
        </Link>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s) => {
          const profile = profiles.find((p) => p.service_type === s.value);
          const status = profile?.service_status ?? "not_activated";
          const live = profile ? isServiceLive(profile) : false;
          const meta = SERVICE_STATUS_META[live ? "active" : status];
          return (
            <div
              key={s.value}
              className={`rounded-2xl border p-4 shadow-soft ${
                live
                  ? s.value === "parking"
                    ? "border-parking bg-parking/5"
                    : "border-brown bg-brand-soft/40"
                  : "border-border bg-card"
              }`}
            >
              <div className="text-sm font-bold text-brown-dark">{SERVICE_LABEL[s.value]}</div>
              <span
                className={`mt-1.5 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}
              >
                {meta.label}
              </span>
              {live ? (
                <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                  <div>{listingCount(s.value)} listings</div>
                  <div>{enquiryCount(s.value)} enquiries</div>
                  <div>Subscription: {profile?.subscription_status}</div>
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  {status === "not_activated"
                    ? "This service is not part of your account."
                    : status === "expired"
                      ? "Renew to continue listing."
                      : "Awaiting completion or admin approval."}
                </div>
              )}
              <Link
                to="/partner/services/$serviceType"
                params={{ serviceType: s.value }}
                className="mt-3 inline-flex rounded-full border border-border px-3 py-1.5 text-[11px] font-semibold text-brown-dark hover:bg-brand-soft"
              >
                {live
                  ? "Manage"
                  : status === "not_activated"
                    ? "Add service"
                    : status === "expired"
                      ? "Renew"
                      : "View status"}
              </Link>
            </div>
          );
        })}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Active services"
          value={activeTypes.length}
          hint="Each billed separately"
          icon={Building2}
        />
        <StatCard
          label="Parking places"
          value={canList("parking") ? parkingRows.length : 0}
          hint={
            canList("parking")
              ? `${availableSlots}/${totalSlots} slots free`
              : "Parking not activated"
          }
          icon={CircleParking}
        />
        <StatCard
          label="Invoices"
          value={invoices.length}
          hint={`${formatMoney(outstanding)} outstanding`}
          icon={ReceiptText}
        />
        <StatCard
          label="Enquiries"
          value={enquiries.length}
          hint="Filter by service"
          icon={MessageSquare}
        />
      </div>

      {propertyServicesActive && (
        <>
          <div className="mt-6 flex items-center justify-between">
            <h2 className="text-base font-bold text-brown-dark">Recent property listings</h2>
            <Link
              to="/partner/properties/new"
              className="inline-flex items-center gap-1.5 rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
            >
              <Plus className="h-4 w-4" /> Add Listing
            </Link>
          </div>
          {properties.length > 0 ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {properties.slice(0, 3).map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          ) : (
            <div className="mt-3">
              <PlaceholderCard
                title="No listings yet"
                description="Add your first listing for an active service to appear on the map."
              />
            </div>
          )}
        </>
      )}

      {canList("parking") && (
        <>
          <div className="mt-6 flex items-center justify-between">
            <h2 className="text-base font-bold text-brown-dark">Recent parking places</h2>
            <Link
              to="/partner/parking/new"
              className="inline-flex items-center gap-1.5 rounded-full bg-parking px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
            >
              <Plus className="h-4 w-4" /> Add Parking
            </Link>
          </div>
          {parking.length > 0 ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {parking.slice(0, 3).map((p) => (
                <ParkingCard key={p.id} parking={p} />
              ))}
            </div>
          ) : (
            <div className="mt-3">
              <PlaceholderCard
                title="No parking places yet"
                description="List a parking place to appear as a PP pin on the map."
              />
            </div>
          )}
        </>
      )}

      {activeTypes.length === 0 && (
        <div className="mt-6">
          <PlaceholderCard
            title="No active service yet"
            description="Complete verification, agreement, subscription and admin approval for your primary service before creating listings."
          >
            <Link
              to="/partner/services"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
            >
              Go to my services
            </Link>
          </PlaceholderCard>
        </div>
      )}
    </DashboardShell>
  );
}
